/**
 * PAT Reports Panel with Multi-Tab Interface
 * 
 * This component provides a tabbed interface for PAT assessments, showing:
 * - Individual phase tabs (Specifications, Design, Coding, Technical & Testing)
 * - Phase-specific rubrics and submissions
 * - JSON report generation for individual phases or consolidated reports
 * - GitHub deployment status and links
 */

import React, { useState, useEffect } from 'react';
import { 
  checkAssignmentPATStatus, 
  getPATReportsForAssignment,
  generatePATReport,
  generateBatchPATReports,
  generateRepositoryName
} from '../api/patReports';
import PATModerationSimplified from './PATModerationSimplified';

const PATReportsPanel = ({ assignmentId, assignmentTitle, token }) => {
  const [patStatus, setPATStatus] = useState(null);
  const [patReports, setPATReports] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    checkPATStatus();
  }, [assignmentId]);

  const checkPATStatus = async () => {
    try {
      setLoading(true);
      const statusResponse = await checkAssignmentPATStatus(token, assignmentId);
      setPATStatus(statusResponse.data);

      if (statusResponse.data.is_pat_assignment) {
        const reportsResponse = await getPATReportsForAssignment(token, assignmentId);
        setPATReports(reportsResponse.data);
      }
    } catch (error) {
      console.error('Error checking PAT status:', error);
      setError('Failed to load PAT information');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSingleReport = async (learnerId, learnerName) => {
    try {
      setGenerating(true);
      setError('');

      const repositoryName = generateRepositoryName(patStatus);
      
      const response = await generatePATReport(token, {
        assignment_id: assignmentId,
        learner_id: learnerId,
        deploy_to_github: true,
        repository_name: repositoryName
      });

      if (response.data.success) {
        alert(`✅ PAT report generated successfully for ${learnerName}!\n\n` +
              `Report Version: ${response.data.version}\n` +
              `GitHub URL: ${response.data.pages_url || 'Processing...'}`);
        
        // Refresh reports data
        checkPATStatus();
      } else {
        setError(`Failed to generate report: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Error generating PAT report:', error);
      console.error('Error details:', {
        response: error.response,
        data: error.response?.data,
        message: error.message,
        stack: error.stack
      });
      
      // Extra detailed logging for debugging
      if (error.response?.data) {
        console.error('Response data details:', JSON.stringify(error.response.data, null, 2));
      }
      
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          `Unknown error (Status: ${error.response?.status || 'N/A'})`;
      
      setError(`Error generating report: ${errorMessage}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateBatchReports = async () => {
    try {
      setGenerating(true);
      setError('');

      const repositoryName = generateRepositoryName(patStatus);
      
      const response = await generateBatchPATReports(token, {
        assignment_id: assignmentId,
        deploy_to_github: true,
        repository_name: repositoryName
      });

      if (response.data.success) {
        alert(`✅ Batch PAT reports generated!\n\n` +
              `Successful: ${response.data.successful_reports}/${response.data.total_learners}\n` +
              `Repository: ${response.data.repository_url || 'Processing...'}`);
        
        // Refresh reports data
        checkPATStatus();
      } else {
        setError(`Failed to generate batch reports: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Error generating batch reports:', error);
      setError(`Error generating batch reports: ${error.response?.data?.error || error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-8 bg-gray-200 rounded w-full"></div>
        </div>
      </div>
    );
  }

  if (!patStatus?.is_pat_assignment) {
    return null; // Not a PAT assignment, don't show this panel
  }

  // Dynamic phase type mapping based on actual data
  const getPhaseIcon = (phaseType) => {
    const iconMap = {
      'specifications': '📋',
      'design': '🎨', 
      'coding': '💻',
      'technical': '🔧',
      'testing': '🧪',
      'technical_testing': '🔧🧪'
    };
    return iconMap[phaseType] || '📄';
  };

  const getPhaseColor = (phaseType) => {
    const colorMap = {
      'specifications': 'blue',
      'design': 'purple',
      'coding': 'green', 
      'technical': 'orange',
      'testing': 'red',
      'technical_testing': 'indigo'
    };
    return colorMap[phaseType] || 'gray';
  };

  const currentPhase = {
    name: patStatus?.phase_name || 'Unknown Phase',
    icon: getPhaseIcon(patStatus?.phase_type),
    color: getPhaseColor(patStatus?.phase_type)
  };

  // Get all phases from the PAT assessment data
  const allPhases = patReports?.pat_assessment?.all_phases || [];

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      {/* PAT Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center">
              <span className="text-2xl mr-2">🎯</span>
              PAT Assessment Reports
            </h3>
            <p className="text-indigo-100 text-sm">
              {patStatus.pat_assessment_name} - {currentPhase.name} Phase
            </p>
          </div>
          <div className="text-right">
            <div className="text-white text-sm">
              {patStatus.academic_year} • {patStatus.grade_level}
            </div>
            <div className="text-indigo-200 text-xs">
              Phase {patStatus.phase_number}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📊 Overview
          </button>
          <button
            onClick={() => setActiveTab('current-phase')}
            className={`py-2 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'current-phase'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {currentPhase.icon} {currentPhase.name}
          </button>
          <button
            onClick={() => setActiveTab('all-phases')}
            className={`py-2 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'all-phases'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🔄 All Phases
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`py-2 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'reports'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📄 Generated Reports
          </button>
          <button
            onClick={() => setActiveTab('moderation')}
            className={`py-2 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'moderation'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            ✍️ Moderation
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="text-red-400">⚠️</div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-blue-600 font-semibold">Assessment Progress</div>
                <div className="text-2xl font-bold text-blue-900">
                  {patReports?.reports_summary?.completion_rate || 0}%
                </div>
                <div className="text-blue-600 text-sm">
                  {patReports?.reports_summary?.reports_generated || 0} of {patReports?.reports_summary?.total_learners || 0} reports
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-green-600 font-semibold">Current Phase</div>
                <div className="text-lg font-bold text-green-900">
                  {currentPhase.name}
                </div>
                <div className="text-green-600 text-sm">
                  Phase {patStatus.phase_number}
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="text-purple-600 font-semibold">Reports System</div>
                <div className="text-lg font-bold text-purple-900">
                  HTML + GitHub
                </div>
                <div className="text-purple-600 text-sm">
                  Browser-based reports
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="text-yellow-400 text-xl mr-3">💡</div>
                <div>
                  <h4 className="font-semibold text-yellow-800">Modern HTML Reporting System</h4>
                  <p className="text-yellow-700 text-sm mt-1">
                    This PAT assessment uses our modern HTML-based reporting system. Reports are automatically 
                    deployed to GitHub Pages and can be viewed in any browser. Fast, reliable, and responsive!
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Current Phase Tab */}
        {activeTab === 'current-phase' && (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">
                {currentPhase.icon} Phase {patStatus.phase_number}: {currentPhase.name}
              </h4>
              <p className="text-gray-600 text-sm">
                This assignment represents the <strong>{currentPhase.name}</strong> phase of the PAT assessment. 
                Reports generated from this page will include data from all phases, but focus on submissions 
                for this specific phase.
              </p>
            </div>

            <div className="space-y-4">
              <h5 className="font-medium text-gray-900">Individual Reports</h5>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="grid grid-cols-4 gap-4 px-4 py-3 bg-gray-50 border-b text-sm font-medium text-gray-700">
                  <div>Learner</div>
                  <div>Status</div>
                  <div>Last Updated</div>
                  <div>Actions</div>
                </div>
                {patReports?.learner_reports?.map((learner, index) => (
                  <div key={index} className="grid grid-cols-4 gap-4 px-4 py-3 border-b border-gray-100">
                    <div>
                      <div className="font-medium">{learner.learner_name}</div>
                      <div className="text-sm text-gray-500">{learner.exam_number || 'No exam number'}</div>
                    </div>
                    <div>
                      {learner.has_report ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          ✅ Report v{learner.version}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          ⏳ No report
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      {learner.generated_at ? new Date(learner.generated_at).toLocaleDateString() : 'Never'}
                    </div>
                    <div>
                      <button
                        onClick={() => handleGenerateSingleReport(learner.learner_id, learner.learner_name)}
                        disabled={generating}
                        className="text-indigo-600 hover:text-indigo-900 text-sm font-medium disabled:opacity-50"
                      >
                        {learner.has_report ? 'Update' : 'Generate'}
                      </button>
                      {learner.github_url && (
                        <a
                          href={learner.github_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-3 text-green-600 hover:text-green-900 text-sm font-medium"
                        >
                          View Report
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* All Phases Tab */}
        {activeTab === 'all-phases' && (
          <div className="space-y-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">📚 Complete PAT Assessment</h4>
              <p className="text-blue-800 text-sm">
                This view shows the complete PAT assessment across all phases. Generated reports will include 
                consolidated data from all phases with detailed breakdowns.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {allPhases.map((phase) => {
                const phaseColor = getPhaseColor(phase.phase_type);
                const phaseIcon = getPhaseIcon(phase.phase_type);
                return (
                  <div key={phase.id} className={`bg-${phaseColor}-50 rounded-lg p-4 border border-${phaseColor}-200`}>
                    <div className="text-2xl mb-2">{phaseIcon}</div>
                    <div className={`font-semibold text-${phaseColor}-900`}>
                      Phase {phase.phase_number}: {phase.phase_name}
                    </div>
                    <div className={`text-${phaseColor}-600 text-sm`}>
                      {phase.is_current ? 'Current Phase' : `${phase.weight_percentage}% weight`}
                    </div>
                    <div className={`text-${phaseColor}-500 text-xs mt-1`}>
                      {phase.max_points} points
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6">
              <h5 className="font-semibold text-gray-900 mb-4">🚀 Batch Report Generation</h5>
              <p className="text-gray-600 text-sm mb-4">
                Generate consolidated PAT reports for all learners in this assessment. Each report will include 
                data from all phases with detailed scoring and feedback.
              </p>
              <button
                onClick={handleGenerateBatchReports}
                disabled={generating}
                className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generating ? (
                  <>
                    <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                    Generating...
                  </>
                ) : (
                  <>
                    🎯 Generate All PAT Reports
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h4 className="font-semibold text-gray-900">📄 Generated Reports</h4>
              <button
                onClick={checkPATStatus}
                className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
              >
                🔄 Refresh
              </button>
            </div>

            {patReports?.learner_reports?.filter(report => report.has_report).length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-400 text-6xl mb-4">📄</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Reports Generated Yet</h3>
                <p className="text-gray-500 mb-4">
                  Generate your first PAT report using the other tabs.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {patReports?.learner_reports?.filter(report => report.has_report).map((report, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h5 className="font-medium text-gray-900">{report.learner_name}</h5>
                        <p className="text-sm text-gray-500">{report.exam_number || 'No exam number'}</p>
                      </div>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        v{report.version}
                      </span>
                    </div>
                    
                    {report.final_percentage !== null && (
                      <div className="mb-3">
                        <div className="text-sm text-gray-600">Final Score</div>
                        <div className="text-lg font-semibold text-gray-900">
                          {report.final_percentage}%
                        </div>
                      </div>
                    )}
                    
                    <div className="text-xs text-gray-500 mb-3">
                      Generated: {new Date(report.generated_at).toLocaleDateString()}
                    </div>
                    
                    <div className="flex space-x-2">
                      {report.github_url && (
                        <a
                          href={report.github_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 bg-green-600 text-white text-center py-2 px-3 rounded text-sm hover:bg-green-700"
                        >
                          🔗 View Report
                        </a>
                      )}
                      <button
                        onClick={() => handleGenerateSingleReport(report.learner_id, report.learner_name)}
                        disabled={generating}
                        className="flex-1 bg-gray-100 text-gray-700 text-center py-2 px-3 rounded text-sm hover:bg-gray-200 disabled:opacity-50"
                      >
                        🔄 Update
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Moderation Tab */}
        {activeTab === 'moderation' && (
          <div className="space-y-6">
            {patStatus && patReports && (
              <PATModerationSimplified
                token={token}
                patAssessment={{
                  id: patStatus.pat_assessment_id,
                  name: patStatus.pat_assessment_name,
                  academic_year: patStatus.academic_year
                }}
                learners={patReports.learner_reports.map(report => ({
                  id: report.learner_id,
                  full_name: report.learner_name,
                  exam_number: report.exam_number
                }))}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PATReportsPanel;